import React, { useEffect, useState } from "react";
import { ReactComponent as Haram } from "../../assets/svg/PaymentMethods/Haram.svg";
import { ReactComponent as BankTransfer } from "../../assets/svg/PaymentMethods/BankTransfer.svg";
import { ReactComponent as CashOnSite } from "../../assets/svg/PaymentMethods/CashOnSite.svg";
import { ReactComponent as QR } from "../../assets/svg/PaymentMethods/QR.svg";
import { useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import axios from "axios";
import { API } from "../../shared/api/Api";
import { useNavigate, useParams } from "react-router-dom";
import { showToast } from "../../utils/utils";

const PurchaseCoursePage = () => {
  const user = useSelector(selectUserInformations);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingExistingRequest, setCheckingExistingRequest] = useState(true);
  const [hasPendingPaymentRequest, setHasPendingPaymentRequest] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  console.log(checkingExistingRequest)

  const handleMethodSelect = (method) => {
    if (hasPendingPaymentRequest) {
      return;
    }
    setSelectedMethod(method);
  };

  useEffect(() => {
    const checkExistingPaymentRequest = async () => {
      if (!user?.token || !id) {
        setCheckingExistingRequest(false);
        return;
      }

      try {
        const { data } = await axios.get(`${API.Courses.app_courses_get}/${id}`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });

        if (data?.status === 200) {
          const pending = Boolean(data?.data?.has_pending_payment_request);
          setHasPendingPaymentRequest(pending);
          if (pending) {
            showToast("Payment Pending", "You already have a pending payment request for this course.", "info");
          }
        }
      } catch (error) {
        console.error("Error checking existing payment request:", error);
      } finally {
        setCheckingExistingRequest(false);
      }
    };

    checkExistingPaymentRequest();
  }, [id, user?.token]);

  const handlePurchase = async () => {
    if (hasPendingPaymentRequest) {
      showToast("Payment Pending", "You already have a pending payment request for this course.", "info");
      return;
    }

    // Validate that user has a student record
    if (!user?.student?.id) {
      showToast("Error", "You must be registered as a student to purchase courses.", "error");
      return;
    }

    // Validate payment method is selected
    if (!selectedMethod) {
      showToast("Error", "Please select a payment method.", "error");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API.Enrollment.app_enrollment_enrollStudent}/${id}`,
        {
          student_id: user.student.id,
          method: selectedMethod,
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (data.status === 200 && data.errorCode === 0) {
        showToast("Purchase Pending", data.message, "success");
        navigate("/dashboard/home");
      } else {
        showToast("Error", data.message || "Failed to enroll in course.", "error");
      }
    } catch (error) {
      console.error("Purchase error:", error);
      showToast(
        "Error", 
        error.response?.data?.message || "An error occurred while processing your purchase.", 
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-screen min-h-screen py-4 sm:py-6 md:py-8 px-2 sm:px-4 md:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row px-4 sm:px-6 md:px-10 py-6 sm:py-8 md:py-10 bg-white rounded-lg shadow-md mb-6">
        <h2 className="font-anek-odia text-2xl sm:text-3xl md:text-5xl font-medium text-primary leading-relaxed">
          Course Payment
        </h2>
      </div>

      {checkingExistingRequest ? (
        <div className="max-w-2xl mx-auto mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-700 text-sm">
          Checking your current payment request status...
        </div>
      ) : hasPendingPaymentRequest ? (
        <div className="max-w-2xl mx-auto mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800 text-sm">
          You already have a pending payment request for this course. Please wait for admin review instead of creating a new request.
        </div>
      ) : null}

      {/* Payment Methods */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 py-8">
        {/* Haram Transfer */}
        <div
          className={`bg-white h-[35rem] rounded-lg border-2 ${
            selectedMethod === "haram_transfer"
              ? "border-blue-500"
              : "border-gray-200"
          } shadow-md overflow-hidden transition-all duration-200 hover:shadow-lg transform hover:-translate-y-1 ${
            hasPendingPaymentRequest ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
          }`}
          onClick={() => handleMethodSelect("haram_transfer")}
        >
          <div className="p-3 sm:p-4 flex flex-col items-center">
            <div className="p-2 sm:p-3 rounded-md mb-2 sm:mb-4 w-full flex items-center justify-center">
              <Haram className="w-full max-w-[200px] h-auto" />
            </div>
            <div className="w-full h-48 sm:h-56 md:h-64 bg-white flex items-center justify-center">
              <div className="w-40 sm:w-48 md:w-56 h-40 sm:h-48 md:h-56 rounded-lg overflow-hidden">
                <QR className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>

        {/* Bank Transfer */}
        <div
          className={`bg-white h-[35rem] rounded-lg border-2 ${
            selectedMethod === "bank_transfer"
              ? "border-blue-500"
              : "border-gray-200"
          } shadow-md overflow-hidden transition-all duration-200 hover:shadow-lg transform hover:-translate-y-1 ${
            hasPendingPaymentRequest ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
          }`}
          onClick={() => handleMethodSelect("bank_transfer")}
        >
          <div className="p-3 sm:p-4 flex flex-col items-center">
            <div className="p-2 sm:p-3 rounded-md mb-2 sm:mb-4 w-full flex items-center justify-center">
              <BankTransfer className="w-full max-w-[200px] h-auto" />
            </div>
            <div className="w-full h-48 sm:h-56 md:h-64 bg-white flex items-center justify-center">
              <div className="w-40 sm:w-48 md:w-56 h-40 sm:h-48 md:h-56 rounded-lg overflow-hidden">
                <QR className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>

        {/* Cash On Site */}
        <div
          className={`bg-white h-auto min-h-[35rem] rounded-lg border-2 ${
            selectedMethod === "cash" ? "border-blue-500" : "border-gray-200"
          } shadow-md overflow-hidden transition-all duration-200 hover:shadow-lg transform hover:-translate-y-1 ${
            hasPendingPaymentRequest ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
          }`}
          onClick={() => handleMethodSelect("cash")}
        >
          <div className="p-3 sm:p-4 flex flex-col items-center h-full">
            <div className="p-2 sm:p-3 rounded-md mb-4 sm:mb-6 w-full flex items-center justify-center">
              <CashOnSite className="w-full max-w-[180px] h-auto" />
            </div>
            <div className="w-full flex-grow flex flex-col items-center justify-start space-y-4 pt-2 sm:pt-4 px-3 sm:px-5">
              {/* Location 1 */}
              <div className="w-full flex flex-row mb-2 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="w-14 sm:w-16 h-auto min-h-[4rem] bg-primary flex items-center justify-center text-white text-xl sm:text-2xl font-bold">
                  1
                </div>
                <div className="flex-1 border border-gray-300 p-3 sm:p-4 flex flex-col justify-center min-h-[4rem]">
                  <p className="font-medium text-sm sm:text-base text-gray-800">
                    DAMASCUS
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    BARAMKEH - AL-MAAZ STREET
                  </p>
                </div>
              </div>

              {/* Location 2 */}
              <div className="w-full flex flex-row rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="w-14 sm:w-16 h-auto min-h-[4rem] bg-primary flex items-center justify-center text-white text-xl sm:text-2xl font-bold">
                  2
                </div>
                <div className="flex-1 border border-gray-300 p-3 sm:p-4 flex flex-col justify-center min-h-[4rem]">
                  <p className="font-medium text-sm sm:text-base text-gray-800">
                    DAMASCUS
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    JARAMANAH
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Button - Optional */}
      {selectedMethod && !hasPendingPaymentRequest && (
        <div className="mt-8 flex justify-center">
          <button
            className="bg-primary text-white font-normal py-3 px-8 rounded-lg text-xl shadow-md hover:bg-blue-700 transition-colors duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handlePurchase}
            disabled={loading}
          >
            {loading ? "Processing..." : "Confirm Payment"}
          </button>
        </div>
      )}
    </div>
  );
};

export default PurchaseCoursePage;
